import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import WordInput from "@/components/WordInput";
import { tierSubsequentLabel, INITIAL_STORY_LIMIT } from "@/lib/wordLimits";

export default function CasePage() {
  const { id } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // key: stage-participant -> text
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [activeParticipant, setActiveParticipant] = useState("A");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${id}`);
      setPayload(data);
      // hydrate drafts
      const dMap = {};
      for (const d of data.drafts || []) {
        dMap[`${d.stage_index}-${d.participant}`] = d.text;
      }
      setDrafts(dMap);
    } catch (e) {
      setErrorMsg(extractError(e, "Could not load case"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // While any stage summary is still being generated in the background,
  // poll the case payload so the UI updates as soon as the LLM result lands.
  useEffect(() => {
    const pending = (payload?.summaries || []).some((s) => s.status === "pending");
    if (!pending) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [payload, load]);

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-[color:var(--text-muted)]" data-testid="case-loading">
        Loading case…
      </div>
    );
  }
  if (!payload) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12" data-testid="case-error">
        <p className="text-[color:var(--counter-error)]">{errorMsg || "Case not found."}</p>
        <Link to="/dashboard" className="text-[color:var(--participant-a)] underline mt-3 inline-block">
          ← Back to dashboard
        </Link>
      </div>
    );
  }

  const { case: c, stages, entries, summaries, limits } = payload;
  const stageIndex = c.current_stage;
  const stageMeta = stages[stageIndex];
  const stageLimit =
    limits.find((l) => l.stage_index === stageIndex)?.limit || INITIAL_STORY_LIMIT;

  const aEntry = entries.find(
    (e) => e.stage_index === stageIndex && e.participant === "A",
  );
  const bEntry = entries.find(
    (e) => e.stage_index === stageIndex && e.participant === "B",
  );

  // Effective participant for this device:
  //  - non-legacy: derived from server (viewer_participant).
  //  - legacy: A/B tab switcher (creator drives both).
  const isLegacy = !!c.legacy_single_user;
  const viewer = c.viewer_participant || "A";
  const effectiveParticipant = isLegacy ? activeParticipant : viewer;
  const currentParticipant = c.current_participant || "A";
  const isAwaitingB = c.status === "awaiting_b";
  const isMyTurn = !isAwaitingB && currentParticipant === effectiveParticipant;
  const offTurnName = currentParticipant === "A" ? c.participant_a_name : c.participant_b_name;

  const completed = c.status === "completed";

  const handleSubmit = async (participantArg, text, isDraft = false) => {
    setSubmitting(true);
    setErrorMsg("");
    try {
      await api.post(`/cases/${id}/turn`, {
        // Legacy cases require participant; modern cases derive from server-side viewer.
        ...(isLegacy ? { participant: participantArg } : {}),
        text,
        is_draft: isDraft,
      });
      await load();
    } catch (e) {
      setErrorMsg(extractError(e, "Could not submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const draftKey = (p) => `${stageIndex}-${p}`;
  const draftValue = (p) =>
    drafts[draftKey(p)] ??
    entries.find((e) => e.stage_index === stageIndex && e.participant === p && e.is_draft !== false)
      ?.text ??
    "";

  const setDraft = (p, v) => setDrafts((d) => ({ ...d, [draftKey(p)]: v }));

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 fade-in" data-testid="case-page">
      <CaseHeader caseDoc={c} stages={stages} />

      {isAwaitingB && (
        <AwaitingBBanner caseDoc={c} onResend={async () => {
          try {
            const { data } = await api.post(`/cases/${id}/resend-invite`);
            // Refresh so the new code surfaces.
            await load();
            return data;
          } catch (e) {
            setErrorMsg(extractError(e, "Could not resend invite"));
            return null;
          }
        }} />
      )}

      {completed && (
        <div
          className="mt-6 rounded-2xl border border-[color:var(--line)] bg-[color:var(--surface-sage)] p-6"
          data-testid="case-completed"
        >
          <div className="font-serif text-2xl">Resolution complete</div>
          <p className="text-sm text-[color:var(--text-muted)] mt-1">
            Both participants have reached the Agreement stage. Scroll below to
            review every stage.
          </p>
        </div>
      )}

      {!completed && !isAwaitingB && (
        <div
          className={`mt-6 rounded-2xl border border-[color:var(--line)] ${
            stageIndex === 0 ? "stage-0-bg" : "bg-white"
          } p-6`}
          data-testid="current-stage-block"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
                Current stage
              </div>
              <h2 className="font-serif text-2xl mt-1">
                Stage {stageIndex}: {stageMeta.name}
              </h2>
            </div>
            <div className="text-xs text-[color:var(--text-muted)] text-right">
              <div>Word limit this turn: <b className="text-[color:var(--text-primary)]">{stageLimit}</b></div>
              <div>
                Tier: <b className="text-[color:var(--text-primary)]">{c.tier}</b> · Subsequent turns:{" "}
                <b className="text-[color:var(--text-primary)]">{tierSubsequentLabel(c.tier, c.custom_word_limit)}</b>
              </div>
            </div>
          </div>

          {stageIndex === 0 ? (
            <div className="rounded-xl bg-white/60 border border-[color:var(--line)] p-4 text-sm text-[color:var(--text-primary)] mb-5" data-testid="stage-0-banner">
              <b>Initial Story Input.</b> You have up to <b>400 words</b> to
              explain your side of the story. Focus on what happened, how it
              affected you, and what you want resolved. Participant A submits
              first, then Participant B. Neither participant can respond
              directly during this stage.
            </div>
          ) : (
            <div className="rounded-xl bg-[color:var(--surface-sage)] border border-[color:var(--line)] p-4 text-sm text-[color:var(--text-primary)] mb-5" data-testid="stage-banner">
              You have up to <b>{stageLimit} words</b> for this turn. Please
              respond clearly and respectfully — no interruptions during the
              other participant's turn.
            </div>
          )}

          {errorMsg && (
            <div className="mb-3 text-sm text-[color:var(--counter-error)]" data-testid="case-error-msg">
              {errorMsg}
            </div>
          )}

          {/* Viewer / participant indicator */}
          <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="participant-bar">
            {(isLegacy ? ["A", "B"] : [effectiveParticipant]).map((p) => {
              const submitted = p === "A" ? !!aEntry : !!bEntry;
              const isActive = isLegacy ? activeParticipant === p : true;
              const name = p === "A" ? c.participant_a_name : c.participant_b_name;
              return (
                <button
                  key={p}
                  type="button"
                  data-testid={`participant-tab-${p}`}
                  onClick={() => isLegacy && setActiveParticipant(p)}
                  disabled={!isLegacy}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition flex items-center gap-2 ${
                    isActive
                      ? p === "A"
                        ? "bg-[color:var(--participant-a)] text-white border-transparent"
                        : "bg-[color:var(--participant-b)] text-white border-transparent"
                      : "bg-white border-[color:var(--line)] text-[color:var(--text-primary)]"
                  } ${!isLegacy ? "cursor-default" : ""}`}
                >
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      p === "A"
                        ? "bg-[color:var(--participant-a-soft)]"
                        : "bg-[color:var(--participant-b-soft)]"
                    }`}
                  />
                  {!isLegacy && "You: "}{name}
                  {submitted && <span className="text-xs opacity-80">✓ submitted</span>}
                </button>
              );
            })}
            {!isLegacy && (
              <span
                className={`text-xs px-2.5 py-1 rounded-full border ${
                  isMyTurn
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-yellow-50 border-yellow-200 text-yellow-800"
                }`}
                data-testid="turn-indicator"
              >
                {isMyTurn ? "Your turn" : `Waiting on ${offTurnName}`}
              </span>
            )}
          </div>

          {/* Input or read-only */}
          {(() => {
            const renderParticipant = isLegacy ? activeParticipant : effectiveParticipant;
            const activeSubmitted =
              renderParticipant === "A" ? !!aEntry : !!bEntry;
            const entry = renderParticipant === "A" ? aEntry : bEntry;
            if (activeSubmitted && entry) {
              return (
                <div
                  className="rounded-xl border border-[color:var(--line)] bg-white p-5"
                  data-testid={`submitted-text-${renderParticipant}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium">
                      {renderParticipant === "A"
                        ? c.participant_a_name
                        : c.participant_b_name}{" "}
                      · submitted
                    </div>
                    <div className="text-xs text-[color:var(--text-muted)]">
                      {entry.word_count} / {stageLimit} words
                    </div>
                  </div>
                  <p className="whitespace-pre-wrap leading-relaxed text-[color:var(--text-primary)]">
                    {entry.text}
                  </p>
                  {!isLegacy && !isMyTurn && (
                    <div className="mt-3 text-xs text-[color:var(--text-muted)]" data-testid="waiting-text">
                      Waiting for {offTurnName} to submit their turn. You'll see
                      a neutral summary as soon as both sides have answered.
                    </div>
                  )}
                </div>
              );
            }
            // Stage 0 rule: B can't submit until A submits (no direct response)
            if (
              stageIndex === 0 &&
              renderParticipant === "B" &&
              !aEntry
            ) {
              return (
                <div
                  className="rounded-xl border border-[color:var(--line)] bg-white p-5 text-sm text-[color:var(--text-muted)]"
                  data-testid="waiting-on-a"
                >
                  {c.participant_a_name} hasn't submitted their Initial Story
                  yet. Participant B can begin after Participant A submits —
                  this avoids direct responses during Stage 0.
                </div>
              );
            }
            // Off-turn (non-legacy): show a locked panel instead of the editor.
            if (!isLegacy && !isMyTurn) {
              return (
                <div
                  className="rounded-xl border border-[color:var(--line)] bg-white p-5 text-sm text-[color:var(--text-muted)]"
                  data-testid="off-turn-lock"
                >
                  <div className="font-medium text-[color:var(--text-primary)] mb-1">
                    It's {offTurnName}'s turn
                  </div>
                  You can't type or submit right now — FairResolver locks the
                  input so each participant gets uninterrupted space. You'll be
                  notified once they submit.
                </div>
              );
            }
            return (
              <WordInput
                stageName={`Stage ${stageIndex}: ${stageMeta.name}`}
                prompt={stageMeta.prompt}
                toneReminder={stageMeta.tone}
                limit={stageLimit}
                accent={renderParticipant === "A" ? "a" : "b"}
                value={draftValue(renderParticipant)}
                onChange={(v) => setDraft(renderParticipant, v)}
                onSubmit={(text) =>
                  handleSubmit(renderParticipant, text, false)
                }
                onSaveDraft={(text) =>
                  handleSubmit(renderParticipant, text, true)
                }
                loading={submitting}
                testIdPrefix={`word-input-${renderParticipant}`}
                submitLabel={
                  stageIndex === 0 ? "Submit My Story" : "Submit Turn"
                }
              />
            );
          })()}
        </div>
      )}

      {/* Past stages summary */}
      <PastStages payload={payload} />
    </div>
  );
}

function CaseHeader({ caseDoc, stages }) {
  const stageIndex = caseDoc.current_stage;
  return (
    <div data-testid="case-header">
      <Link
        to="/dashboard"
        className="text-sm text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
        data-testid="case-back-link"
      >
        ← Back to dashboard
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-3 mt-2">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--participant-a)] font-medium">
            Case · {caseDoc.share_code}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl mt-1 text-[color:var(--text-primary)]">
            {caseDoc.title}
          </h1>
          {caseDoc.description && (
            <p className="text-sm text-[color:var(--text-muted)] mt-1 max-w-2xl">
              {caseDoc.description}
            </p>
          )}
        </div>
        <div className="text-sm text-[color:var(--text-muted)] text-right">
          <div>
            <span className="inline-flex items-center gap-2 mr-3">
              <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--participant-a)]" />
              {caseDoc.participant_a_name}
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--participant-b)]" />
              {caseDoc.participant_b_name}
            </span>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-2" data-testid="case-stepper">
        {stages.map((s, i) => {
          const state =
            i < stageIndex ? "done" : i === stageIndex ? "current" : "future";
          return (
            <div
              key={s.key}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${
                state === "current"
                  ? "bg-[color:var(--participant-a)] text-white border-transparent"
                  : state === "done"
                    ? "bg-white border-[color:var(--line)] text-[color:var(--text-primary)]"
                    : "bg-transparent border-[color:var(--line)] text-[color:var(--text-muted)]"
              }`}
              data-testid={`stepper-${s.key}`}
            >
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${
                  state === "done"
                    ? "bg-[color:var(--participant-a)]"
                    : "bg-white/70"
                }`}
              />
              {i}. {s.name}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PastStages({ payload }) {
  const { case: c, stages, entries, summaries, limits } = payload;
  // Build a per-stage view
  const rows = useMemo(() => {
    return stages
      .map((s) => {
        const a = entries.find(
          (e) => e.stage_index === s.index && e.participant === "A",
        );
        const b = entries.find(
          (e) => e.stage_index === s.index && e.participant === "B",
        );
        const sum = summaries.find((x) => x.stage_index === s.index);
        const limit =
          limits.find((l) => l.stage_index === s.index)?.limit || 0;
        return { stage: s, a, b, sum, limit };
      })
      .filter(({ stage }) => stage.index < c.current_stage || c.status === "completed");
  }, [stages, entries, summaries, limits, c.current_stage, c.status]);

  if (rows.length === 0) return null;

  return (
    <div className="mt-10 space-y-6" data-testid="past-stages">
      <h2 className="font-serif text-2xl">Conversation so far</h2>
      {rows.map(({ stage, a, b, sum, limit }) => (
        <div
          key={stage.key}
          className="rounded-2xl border border-[color:var(--line)] bg-white p-5"
          data-testid={`past-stage-${stage.key}`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="font-serif text-xl">
              Stage {stage.index}: {stage.name}
            </div>
            <div className="text-xs text-[color:var(--text-muted)]">Limit: {limit} words</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <ParticipantBlock
              accent="a"
              name={c.participant_a_name}
              entry={a}
              limit={limit}
            />
            <ParticipantBlock
              accent="b"
              name={c.participant_b_name}
              entry={b}
              limit={limit}
            />
          </div>
          {sum && (
            <div
              className="mt-4 rounded-xl border bg-[color:var(--ai-bg)] p-4 text-sm text-[color:var(--text-primary)]"
              style={{ borderColor: "var(--ai-line)" }}
              data-testid={`ai-summary-${stage.key}`}
              data-summary-status={sum.status || "ready"}
            >
              <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)] mb-1 flex items-center gap-2">
                <span>Neutral AI summary</span>
                {sum.status === "pending" && (
                  <span
                    className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full bg-white border border-[color:var(--ai-line)]"
                    data-testid={`ai-summary-pending-${stage.key}`}
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--participant-a)] animate-pulse" />
                    Generating…
                  </span>
                )}
              </div>
              {sum.status === "pending" ? (
                <p className="italic text-[color:var(--text-muted)] leading-relaxed">
                  Our neutral AI mediator is reading both sides and writing a fair, short summary. This usually takes 5–10 seconds.
                </p>
              ) : (
                <p className="whitespace-pre-wrap leading-relaxed">{sum.text}</p>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ParticipantBlock({ accent, name, entry, limit }) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        accent === "a"
          ? "border-[color:var(--participant-a-soft)] bg-[color:var(--participant-a-soft)]/30"
          : "border-[color:var(--participant-b-soft)] bg-[color:var(--participant-b-soft)]/30"
      }`}
      data-testid={`participant-block-${accent}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium flex items-center gap-2">
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              accent === "a"
                ? "bg-[color:var(--participant-a)]"
                : "bg-[color:var(--participant-b)]"
            }`}
          />
          {name}
        </div>
        {entry && (
          <div className="text-xs text-[color:var(--text-muted)]">
            {entry.word_count} / {limit} words
          </div>
        )}
      </div>
      {entry ? (
        <p className="text-sm whitespace-pre-wrap leading-relaxed text-[color:var(--text-primary)]">
          {entry.text}
        </p>
      ) : (
        <p className="text-xs text-[color:var(--text-muted)]">Not submitted.</p>
      )}
    </div>
  );
}


function AwaitingBBanner({ caseDoc, onResend }) {
  const [copied, setCopied] = useState(false);
  const [resending, setResending] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // Tick every second so the countdown moves.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const joinLink = caseDoc.join_link ||
    (typeof window !== "undefined" && caseDoc.invite_code
      ? `${window.location.origin}/join?code=${caseDoc.invite_code}`
      : "");

  const sentAtIso = caseDoc.invite_sent_at || caseDoc.created_at;
  const sentAtMs = sentAtIso ? new Date(sentAtIso).getTime() : null;
  const elapsedSec = sentAtMs ? Math.max(0, Math.floor((now - sentAtMs) / 1000)) : null;
  const RESEND_WINDOW = 5 * 60; // 5 minutes
  const cooldownRemaining = elapsedSec == null ? 0 : Math.max(0, RESEND_WINDOW - elapsedSec);
  const cooldownActive = cooldownRemaining > 0;
  const mmss = (s) => `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;

  const copy = async () => {
    if (!joinLink) return;
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) { console.warn("Could not copy invite link:", err); }
  };

  const handleResend = async () => {
    setResending(true);
    try { await onResend(); } finally { setResending(false); }
  };

  // Only Participant A (creator) gets the invite controls.
  const isCreator = caseDoc.viewer_participant === "A";
  if (!isCreator) {
    return (
      <div
        className="mt-6 rounded-2xl border border-yellow-200 bg-yellow-50 text-yellow-900 p-5"
        data-testid="awaiting-b-non-creator"
      >
        <div className="font-medium">Waiting for the case creator</div>
        <p className="text-sm mt-1">
          This case hasn't been activated yet. The creator needs to confirm
          Participant B has joined.
        </p>
      </div>
    );
  }

  return (
    <div
      className="mt-6 rounded-2xl border border-[color:var(--line)] bg-[color:var(--surface-sage)] p-6"
      data-testid="awaiting-b-banner"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--participant-b)] font-medium">
            Waiting on Participant B
          </p>
          <h2 className="font-serif text-2xl mt-1">
            Invite {caseDoc.participant_b_email}
          </h2>
          <p className="text-sm text-[color:var(--text-muted)] mt-1">
            Share the 6-digit code below — Participant B enters it at the join
            link to sign in on their own device. Until they join, no one can
            type into the case.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">
            Invite code
          </div>
          <div
            className="font-serif text-4xl tracking-[0.35em] text-[color:var(--text-primary)] mt-1"
            data-testid="awaiting-invite-code"
          >
            {caseDoc.invite_code || "------"}
          </div>
        </div>
      </div>
      {joinLink && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <code
            className="text-xs bg-white border border-[color:var(--line)] rounded-lg px-3 py-2 break-all flex-1 min-w-[260px]"
            data-testid="awaiting-join-link"
          >
            {joinLink}
          </code>
          <button
            type="button"
            onClick={copy}
            className="px-3 py-2 rounded-full bg-white border border-[color:var(--line)] text-xs font-medium hover:bg-white/70"
            data-testid="awaiting-copy-link"
          >
            {copied ? "Copied!" : "Copy link"}
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className={`px-3 py-2 rounded-full text-xs font-semibold transition disabled:opacity-50 ${
              cooldownActive
                ? "bg-white text-[color:var(--text-primary)] border border-[color:var(--line)] hover:bg-[color:var(--surface-sage)]"
                : "bg-[color:var(--participant-a)] text-white hover:opacity-90 ring-2 ring-[color:var(--participant-a)]/30"
            }`}
            data-testid="awaiting-resend"
            title={cooldownActive
              ? `Recommended after ${mmss(cooldownRemaining)} — click anyway if you know it's lost.`
              : "Resend the invite email to Participant B"}
          >
            {resending
              ? "Regenerating…"
              : cooldownActive
                ? `Resend in ${mmss(cooldownRemaining)}`
                : "Resend code & email"}
          </button>
        </div>
      )}
      <p className="text-[11px] text-[color:var(--text-muted)] mt-3" data-testid="awaiting-status-line">
        {elapsedSec != null && (
          <>
            Last sent {elapsedSec < 60 ? "just now" : `${Math.floor(elapsedSec / 60)} min ago`}
            {" · "}
          </>
        )}
        {cooldownActive
          ? `Most inbox providers deliver within ~30s. If Participant B hasn't received it in 5 minutes (${mmss(cooldownRemaining)} remaining), regenerate the code.`
          : "5 minutes elapsed — recommend regenerating the code and resending now."}
      </p>
    </div>
  );
}
